const FILLER_WORDS = ['um', 'uh', 'like', "it's"];

const WORD_TO_NUMBER: Record<string, number> = {
  zero: 0,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  thirteen: 13,
  fourteen: 14,
  fifteen: 15,
  sixteen: 16,
  seventeen: 17,
  eighteen: 18,
  nineteen: 19,
  twenty: 20,
  thirty: 30,
  forty: 40,
  fifty: 50,
  sixty: 60,
  seventy: 70,
  eighty: 80,
  ninety: 90,
  hundred: 100,
};

// Map single digit words to their digit characters for concatenation
const DIGIT_WORD_TO_CHAR: Record<string, string> = {
  zero: '0',
  one: '1',
  two: '2',
  three: '3',
  four: '4',
  five: '5',
  six: '6',
  seven: '7',
  eight: '8',
  nine: '9',
};

function stripFillers(tokens: string[]): string[] {
  return tokens.filter((t) => !FILLER_WORDS.includes(t));
}

function parseWordNumber(tokens: string[]): number | null {
  if (tokens.length === 0) return null;

  // "one hundred" or "hundred"
  const hundredIdx = tokens.indexOf('hundred');
  if (hundredIdx !== -1) {
    let multiplier = 1;
    if (hundredIdx > 0) {
      const before = WORD_TO_NUMBER[tokens[hundredIdx - 1]];
      if (before !== undefined && before >= 1 && before <= 9) {
        multiplier = before;
      }
    }
    const base = multiplier * 100;
    const rest = tokens.slice(hundredIdx + 1);
    if (rest.length === 0) return base;
    const remainder = parseWordNumber(rest);
    if (remainder === null) return null;
    return base + remainder;
  }

  // Handles: "twenty one", "forty two", or a single word like "twelve"
  if (tokens.length === 1) {
    const val = WORD_TO_NUMBER[tokens[0]];
    return val !== undefined ? val : null;
  }

  if (tokens.length === 2) {
    const tens = WORD_TO_NUMBER[tokens[0]];
    const ones = WORD_TO_NUMBER[tokens[1]];
    if (tens !== undefined && ones !== undefined && tens >= 20 && ones >= 1 && ones <= 9) {
      return tens + ones;
    }
    return null;
  }

  return null;
}

function parseSingleNumber(tokens: string[]): number | null {
  if (tokens.length === 0) return null;

  // 1. Try direct integer parse on a single token (handles "12", "7", "100")
  if (tokens.length === 1) {
    const direct = parseInt(tokens[0], 10);
    if (!isNaN(direct)) return direct;
  }

  // 2. Try word-to-number lookup
  const wordResult = parseWordNumber(tokens);
  if (wordResult !== null) return wordResult;

  // 3. Try concatenated single digits ("one two" → "12")
  const digitChars = tokens.map((t) => DIGIT_WORD_TO_CHAR[t]);
  if (digitChars.every((c) => c !== undefined)) {
    const concatenated = parseInt(digitChars.join(''), 10);
    if (!isNaN(concatenated)) return concatenated;
  }

  return null;
}

export function parseSpokenNumber(transcript: string): number | null {
  if (!transcript) return null;

  const normalized = transcript.trim().toLowerCase().replace(/-/g, ' ');
  if (!normalized) return null;

  let tokens = normalized.split(/\s+/);
  tokens = stripFillers(tokens);

  if (tokens.length === 0) return null;

  // Try parsing the full token list first
  const full = parseSingleNumber(tokens);
  if (full !== null) return full;

  // Fallback: extract the LAST number found anywhere in the transcript.
  // Handles noise like "team 12", bleed like "11 19", etc.
  const numberMatches = normalized.match(/\d+/g);
  if (numberMatches && numberMatches.length >= 1) {
    const last = parseInt(numberMatches[numberMatches.length - 1], 10);
    if (!isNaN(last)) return last;
  }

  return null;
}
